import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import Layout from "../Layout";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import { notify } from "@/components/common/Notification";
import { toast } from "sonner";
import navigationRoutes from "@/project/propertyPortal/utils/navigationRoutes";
import { Trash2, Edit2 } from "lucide-react";
import CommonModal from "@/components/common/CommonModal";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

// Field name to display label mapping for comparison property fields
const COMPARISON_FIELD_LABELS: Record<string, string> = {
  propertyname: "Property name",
  yearbuilt: "Year built",
  persqftprice: "Per Sqft Price",
  yearsold: "Year sold",
  bed: "Bed",
  bath: "Bath",
  sqfootage: "Sq. Footage",
  farfrom: "Far from main property",
  salesprice: "Sales price",
  notes: "Notes",
};

// Helper function to get field label for comparison property fields
const getComparisonFieldLabel = (fieldName: string): string => {
  return COMPARISON_FIELD_LABELS[fieldName.toLowerCase()] || fieldName;
};

// Fields that are allowed to be edited
const EDITABLE_FIELDS = [
  "propertyname",
  "sqfootage",
  "bed",
  "bath",
  "yearbuilt",
  "persqftprice",
  "rougharv",
  "finalarv",
  "notes",
];

const isFileLikeObject = (value: any): boolean => {
  if (!value || typeof value !== "object") {
    return false;
  }
  return (
    "file_path" in value ||
    "file_original_name" in value ||
    "url" in value ||
    "file_id" in value ||
    "uid" in value ||
    "id" in value
  );
};

const extractFileIdentifier = (value: any): string | null => {
  if (typeof value === "string") {
    return value;
  }
  if (!value || typeof value !== "object") {
    return null;
  }
  return (
    value.id ??
    value.file_id ??
    value.file_path ??
    value.file_path_optimized ??
    value.url ??
    value.uid ??
    value.name ??
    null
  );
};

const sanitizeFileFields = (data: Record<string, any>): Record<string, any> => {
  const sanitized: Record<string, any> = { ...data };

  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];

    if (value === undefined) {
      delete sanitized[key];
      return;
    }

    if (Array.isArray(value)) {
      if (value.some(item => isFileLikeObject(item))) {
        const cleaned = value
          .map(item => extractFileIdentifier(item))
          .filter((item): item is string => !!item);
        sanitized[key] = cleaned;
      }
      return;
    }

    if (isFileLikeObject(value)) {
      const identifier = extractFileIdentifier(value);
      sanitized[key] = identifier ?? value;
    }
  });

  return sanitized;
};

const sanitizeComponentProps = (
  props?: Record<string, any>
): Record<string, any> | undefined => {
  if (!props) {
    return props;
  }
  if (Object.prototype.hasOwnProperty.call(props, "is-in-grid")) {
    const { ["is-in-grid"]: _removed, ...rest } = props;
    return rest;
  }
  return props;
};

// Check if a property is a layout component (Card, FormTab, FormLayout, etc.)
const isLayoutComponent = (property: any): boolean => {
  if (!property || typeof property !== "object") {
    return false;
  }

  // Layout components are typically void type with x-component
  const isVoid = property.type === "void";
  const hasXComponent =
    property["x-component"] && typeof property["x-component"] === "string";

  // Common layout components
  const layoutComponents = [
    "Card",
    "FormTab",
    "FormLayout",
    "FormGrid",
    "Tabs",
    "FormItem",
  ];
  const componentName = property["x-component"];

  return isVoid && hasXComponent && layoutComponents.includes(componentName);
};

// Recursively modify schema to only show editable fields (hide all others)
const modifySchemaToRestrictFields = (schema: any): any => {
  if (!schema || typeof schema !== "object") {
    return schema;
  }

  // Handle schema with nested schema property
  if (schema.schema) {
    return {
      ...schema,
      schema: modifySchemaToRestrictFields(schema.schema),
    };
  }

  // If it's a property definition
  if (schema.properties) {
    const modifiedProperties: any = {};
    Object.keys(schema.properties).forEach(key => {
      const property = schema.properties[key];

      // Check if this is a layout component (Card, FormTab, etc.)
      if (isLayoutComponent(property)) {
        // Keep layout components and recursively process their properties
        const modifiedLayout = modifySchemaToRestrictFields(property);
        // Only include layout component if it has properties after filtering
        if (
          modifiedLayout?.properties &&
          Object.keys(modifiedLayout.properties).length > 0
        ) {
          const sanitizedProps = sanitizeComponentProps(
            modifiedLayout["x-component-props"]
          );
          if (sanitizedProps !== modifiedLayout["x-component-props"]) {
            modifiedLayout["x-component-props"] = sanitizedProps;
          }
          modifiedProperties[key] = modifiedLayout;
        }
      } else {
        const isEditable = EDITABLE_FIELDS.some(
          field => field.toLowerCase() === key.toLowerCase()
        );

        // Only include fields that are in the editable list
        if (isEditable) {
          const modifiedProperty = modifySchemaToRestrictFields(property);
          const sanitizedProps = sanitizeComponentProps(
            modifiedProperty["x-component-props"]
          );
          if (sanitizedProps !== modifiedProperty["x-component-props"]) {
            modifiedProperty["x-component-props"] = sanitizedProps;
          }

          // Fields to disable
          const disabledFields = [
            "propertyname",
            "sqfootage",
            "bed",
            "bath",
            "yearbuilt",
            "persqftprice",
            "rougharv",
            "finalarv",
            "notes",
          ];

          // Make specified fields disabled (read-only)
          if (
            disabledFields.some(
              field => field.toLowerCase() === key.toLowerCase()
            )
          ) {
            modifiedProperty["x-component-props"] = {
              ...modifiedProperty["x-component-props"],
              disabled: true,
              readOnly: true,
            };
          }

          modifiedProperties[key] = modifiedProperty;
        }
        // Completely exclude non-editable fields
      }
    });

    // Return modified schema with filtered properties
    return {
      ...schema,
      properties: modifiedProperties,
    };
  }

  // If it's an array of items
  if (schema.items) {
    return {
      ...schema,
      items: modifySchemaToRestrictFields(schema.items),
    };
  }

  // Recursively process other object properties
  const modified: any = { ...schema };
  Object.keys(schema).forEach(key => {
    if (
      key !== "properties" &&
      key !== "items" &&
      key !== "schema" &&
      typeof schema[key] === "object"
    ) {
      modified[key] = modifySchemaToRestrictFields(schema[key]);
    }
  });
  return modified;
};

const PP_EditComparisonProperty: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const schemaName = "Property Master";
  const comparePropertySchemaName = "Compare Property";

  const form = useMemo(() => createForm({}), []);

  // Modal and comparison properties state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comparisonProperties, setComparisonProperties] = useState<any[]>([]);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(
    null
  );
  const compareForm = useMemo(() => createForm({}), []);
  const compareFormRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const lastSavedArvRef = useRef<number | null>(null);

  // Local state for modal form fields to ensure reactivity
  const [modalFormValues, setModalFormValues] = useState<{
    propertyname: string;
    persqftprice: string;
    bed: string;
    sqfootage: string;
    address: string;
    farfrom: string;
    notes: string;
    yearbuilt: string;
    yearsold: string;
    bath: string;
    salesprice: string;
  }>({
    propertyname: "",
    persqftprice: "",
    bed: "",
    sqfootage: "",
    address: "",
    farfrom: "",
    notes: "",
    yearbuilt: "",
    yearsold: "",
    bath: "",
    salesprice: "",
  });

  // Handle address change for comparison property form
  const handleCompareAddressChange = useCallback(
    (details?: any) => {
      const formattedAddress =
        typeof details === "string"
          ? details
          : (details?.formatted_address ??
            details?.display_name ??
            details?.name ??
            "");
      compareForm.setValuesIn("address", formattedAddress);
      // Also update field state for reactivity
      compareForm.setFieldState("address", state => {
        state.value = formattedAddress;
      });
    },
    [compareForm]
  );

  // Define required fields for comparison property (excluding: notes)
  const requiredComparisonFields = useMemo(
    () => [
      "propertyname", // Property name
      "yearbuilt", // Year built
      "persqftprice", // Per Sqft Price
      "yearsold", // Year sold
      "bed", // Bed
      "bath", // Bath
      "sqfootage", // Sq. Footage
      "farfrom", // Far from main property (field name in schema is "farfrom")
      "salesprice", // Sales price
      // Note: notes is optional
    ],
    []
  );

  // Define numeric-only fields (all except propertyname and notes)
  const numericComparisonFields = useMemo(
    () => [
      "yearbuilt",
      "persqftprice",
      "yearsold",
      "bed",
      "bath",
      "sqfootage",
      "farfrom",
      "salesprice",
    ],
    []
  );

  // Set up required field validators for comparison property form
  useEffect(() => {
    requiredComparisonFields.forEach(fieldName => {
      compareForm.setFieldState(fieldName, state => {
        state.required = true;
        state.validator = (value: any) => {
          const fieldLabel = getComparisonFieldLabel(fieldName);

          if (
            value === undefined ||
            value === null ||
            value === "" ||
            (typeof value === "string" && value.trim() === "") ||
            (typeof value === "number" && isNaN(value))
          ) {
            return `${fieldLabel} is required`;
          }

          // Numeric validation (propertyname is allowed to be text, notes is optional)
          if (numericComparisonFields.includes(fieldName)) {
            const stringValue =
              typeof value === "number" ? String(value) : String(value).trim();
            if (stringValue !== "" && !/^-?\d*\.?\d+$/.test(stringValue)) {
              return `${fieldLabel} must be a valid number`;
            }

            // Fields that should be whole numbers
            const integerFields = ["yearbuilt", "yearsold", "bed", "bath"];
            if (integerFields.includes(fieldName)) {
              const numValue = parseFloat(stringValue);
              if (!Number.isInteger(numValue)) {
                return `${fieldLabel} must be a whole number`;
              }
              if (numValue < 0) {
                return `${fieldLabel} must be a positive number`;
              }
            }
          }

          return undefined;
        };
      });
    });

    // Swap the address field component to Google Places autocomplete
    compareForm.setFieldState("address", state => {
      state.component = GoogleAddressInput as any;
      state.componentProps = {
        ...state.componentProps,
        placeholder: "Search address or place",
        onChange: handleCompareAddressChange,
        types: ["establishment", "geocode"], // Allow both businesses/establishments and addresses
      };
    });
  }, [
    compareForm,
    numericComparisonFields,
    requiredComparisonFields,
    handleCompareAddressChange,
  ]);

  // Get schema columns first to know what fields to fetch
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", comparePropertySchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          comparePropertySchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!comparePropertySchemaName,
  });

  // Load comparison properties from backend
  const {
    data: comparisonPropertiesData,
    refetch: refetchComparisonProperties,
  } = useQuery({
    queryKey: [
      "comparison-properties",
      comparePropertySchemaName,
      columnGridData,
      id,
    ],
    queryFn: async () => {
      if (!columnGridData?.grid || !id) {
        return { results: [], count: 0 };
      }

      // Get all column names from schema (all fields including system fields)
      // Keep original case for field names
      const allColumns = columnGridData.grid.map(
        (field: any) => field.field_name
      );

      // Explicitly add "id" if not already present (only once, lowercase)
      const lowerColumns = allColumns.map((col: string) => col.toLowerCase());
      if (!lowerColumns.includes("id")) {
        allColumns.push("id");
      }

      console.log("All columns from schema:", allColumns);

      // Fetch all comparison properties with all schema fields
      let response;
      try {
        response = await DynamicModuleService.getGridCoByConfig(
          comparePropertySchemaName,
          1,
          1000,
          allColumns, // Request all columns from schema
          {
            propertymaster_id: id,
          }
        );

        // If ID is not in the response, try fetching without column restrictions
        if (
          response?.results?.[0] &&
          !(
            "id" in response.results[0] ||
            "Id" in response.results[0] ||
            "ID" in response.results[0]
          )
        ) {
          console.warn(
            "ID not found in response, trying without column restrictions..."
          );
          try {
            const fullResponse = await DynamicModuleService.getGridCoByConfig(
              comparePropertySchemaName,
              1,
              1000,
              [], // Empty array to get all fields
              {
                propertymaster_id: id,
              }
            );
            if (
              fullResponse?.results?.[0] &&
              ("id" in fullResponse.results[0] ||
                "Id" in fullResponse.results[0] ||
                "ID" in fullResponse.results[0])
            ) {
              response = fullResponse;
            }
          } catch (fallbackError) {
            console.error("Error in fallback fetch:", fallbackError);
            // Continue with original response even if fallback fails
          }
        }
      } catch (error) {
        console.error("Error fetching comparison properties:", error);
        // Return empty result instead of throwing to prevent query from breaking
        return { results: [], count: 0 };
      }

      console.log("Comparison properties response:", response);
      console.log(
        "First item keys:",
        response?.results?.[0] ? Object.keys(response.results[0]) : "No items"
      );
      console.log("First item full data:", response?.results?.[0]);

      // Ensure we always return a valid response structure
      return response ?? { results: [], count: 0 };
    },
    enabled: !!comparePropertySchemaName && !!columnGridData && !!id,
  });

  // Update state when data is loaded
  useEffect(() => {
    if (comparisonPropertiesData) {
      // Handle different response structures
      const data =
        comparisonPropertiesData.results ??
        comparisonPropertiesData.data ??
        comparisonPropertiesData;

      let propertiesArray: any[] = [];
      if (Array.isArray(data)) {
        propertiesArray = data;
      } else if (data && Array.isArray(data.results)) {
        propertiesArray = data.results;
      } else {
        console.warn("Unexpected data structure:", comparisonPropertiesData);
        setComparisonProperties([]);
        return;
      }

      // Normalize all property objects to have lowercase keys for consistency
      const normalizedProperties = propertiesArray.map((property: any) => {
        const normalized: any = {};
        Object.keys(property).forEach(key => {
          normalized[key.toLowerCase()] = property[key];
        });
        return normalized;
      });

      console.log("Setting comparison properties:", normalizedProperties);
      setComparisonProperties(normalizedProperties);
    } else {
      setComparisonProperties([]);
    }
  }, [comparisonPropertiesData]);

  const { data: schemaJson } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
    enabled: !!schemaName,
  });

  // Fetch main property details when page opens
  const { data: recordInfo } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: async () => {
      if (!schemaName || !id) {
        return null;
      }
      try {
        const data = await DynamicModuleService.getItemById(schemaName, id);
        console.log("Fetched Property Master details:", data);
        return data;
      } catch (error) {
        console.error("Error fetching Property Master details:", error);
        notify.error("Failed to load property details. Please try again.");
        return null;
      }
    },
    enabled: !!schemaName && !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Modify schema to restrict fields
  const restrictedSchema = useMemo(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.json ?? schemaJson;

    // Debug: Log original schema structure
    if (
      schema &&
      typeof schema === "object" &&
      "properties" in schema &&
      schema.properties &&
      typeof schema.properties === "object" &&
      schema.properties &&
      Object.keys(schema.properties).length > 0
    ) {
      console.log("Original schema fields:", Object.keys(schema.properties));
    }

    const modified = modifySchemaToRestrictFields(schema);

    // Debug: Log the modified schema to see what fields are available
    if (
      modified &&
      typeof modified === "object" &&
      "properties" in modified &&
      modified.properties &&
      typeof modified.properties === "object" &&
      modified.properties &&
      Object.keys(modified.properties).length > 0
    ) {
      console.log("Modified schema fields:", Object.keys(modified.properties));
      console.log("Editable fields we're looking for:", EDITABLE_FIELDS);

      // Check if we found any editable fields
      const foundFields = Object.keys(modified.properties).filter(key =>
        EDITABLE_FIELDS.some(field => field.toLowerCase() === key.toLowerCase())
      );
      console.log("Found editable fields:", foundFields);
    } else {
      console.warn("Modified schema has no properties or is null");
    }

    return modified;
  }, [schemaJson]);

  // Set initial values when record is loaded
  useEffect(() => {
    if (recordInfo) {
      const initialData = removeMetaFields(recordInfo);
      form.setInitialValues(initialData);
      // Also set current values to ensure form fields are populated
      Object.keys(initialData).forEach(key => {
        form.setFieldState(key, state => {
          state.value = initialData[key];
        });
      });
    }
  }, [recordInfo, form]);

  // Disable only Final ARV field (it's calculated automatically)
  useEffect(() => {
    form.setFieldState("finalarv", state => {
      state.componentProps = {
        ...state.componentProps,
        disabled: true,
        readOnly: true,
      };
    });
  }, [form]);

  // Track last saved ARV from record info
  useEffect(() => {
    if (recordInfo) {
      const finalArvKey = Object.keys(recordInfo ?? {}).find(
        key => key.toLowerCase() === "finalarv"
      );
      const finalArvValue =
        finalArvKey && recordInfo[finalArvKey] !== undefined
          ? recordInfo[finalArvKey]
          : null;
      const numericFinalArv =
        typeof finalArvValue === "number"
          ? finalArvValue
          : typeof finalArvValue === "string"
            ? parseFloat(finalArvValue)
            : null;
      lastSavedArvRef.current =
        typeof numericFinalArv === "number" && !Number.isNaN(numericFinalArv)
          ? numericFinalArv
          : null;
    }
  }, [recordInfo]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await form.validate();
      const values = removeMetaFields(form.values);

      if (!recordInfo) {
        notify.error("Record data not loaded. Please try again.");
        return;
      }

      // Start with the original record data to preserve all fields
      const originalData = removeMetaFields(recordInfo);
      const updateData = { ...originalData };

      // Only update the editable fields from the form values
      EDITABLE_FIELDS.forEach(field => {
        const fieldKey = Object.keys(values).find(
          key => key.toLowerCase() === field.toLowerCase()
        );
        if (fieldKey && values[fieldKey] !== undefined) {
          updateData[fieldKey] = values[fieldKey];
        }
      });

      // Ensure update payload carries the latest known ARV value
      updateData.finalarv =
        typeof lastSavedArvRef.current === "number" &&
        !Number.isNaN(lastSavedArvRef.current)
          ? lastSavedArvRef.current
          : null;

      // Extract only the editable fields for change detection (excluding finalarv since it's auto-calculated)
      const restrictedValues: any = {};
      EDITABLE_FIELDS.forEach(field => {
        // Skip finalarv in change detection since it's auto-calculated
        if (field.toLowerCase() === "finalarv") {
          return;
        }
        const fieldKey = Object.keys(updateData).find(
          key => key.toLowerCase() === field.toLowerCase()
        );
        if (fieldKey && updateData[fieldKey] !== undefined) {
          restrictedValues[fieldKey] = updateData[fieldKey];
        }
      });

      // Check for changes before updating (only check editable fields)
      if (!checkFormChanges(restrictedValues, recordInfo, notify)) {
        return;
      }

      // Send the complete record with all fields (original + updated editable fields + calculated finalarv)
      const sanitizedPayload = sanitizeFileFields(updateData);

      console.log("Updating Property Master with payload:", sanitizedPayload);

      await DynamicModuleService.updateItem(
        schemaName,
        id ?? "",
        sanitizedPayload
      );
      toast.success("Property updated successfully!");

      // Invalidate queries
      await queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey;
          return (
            (Array.isArray(key) &&
              key[0] === "moduleData" &&
              key[1] === schemaName) ||
            (Array.isArray(key) &&
              key[0] === "columnGridData" &&
              key[1] === schemaName)
          );
        },
      });

      navigate(navigationRoutes.COMPARISON_PROPERTY_MASTER);
    } catch (error: any) {
      console.error(
        "Save error:",
        error?.response?.data ?? error?.message ?? error
      );
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to update property."
      );
    }
  };

  const handleCompareProperty = () => {
    setEditingPropertyId(null);
    setIsModalOpen(true);
    compareForm.reset();
    // Reset local form values
    setModalFormValues({
      propertyname: "",
      persqftprice: "",
      bed: "",
      sqfootage: "",
      address: "",
      farfrom: "",
      notes: "",
      yearbuilt: "",
      yearsold: "",
      bath: "",
      salesprice: "",
    });
  };

  const handleEditComparisonProperty = (property: any) => {
    // Try to find the ID field (case-insensitive)
    const propertyId =
      property.id ??
      property.Id ??
      property.ID ??
      property._id ??
      property._Id ??
      property._ID ??
      (() => {
        const idKey = Object.keys(property ?? {}).find(
          key => key.toLowerCase() === "id"
        );
        return idKey ? property[idKey] : null;
      })();

    if (!propertyId) {
      notify.error("Cannot edit: Property ID not found");
      return;
    }

    setEditingPropertyId(String(propertyId));
    setIsModalOpen(true);

    // Load property data into form and local state
    const formValues: any = {};
    const localValues: any = {
      propertyname: "",
      persqftprice: "",
      bed: "",
      sqfootage: "",
      address: "",
      farfrom: "",
      notes: "",
      yearbuilt: "",
      yearsold: "",
      bath: "",
      salesprice: "",
    };

    Object.keys(property).forEach(key => {
      const value = property[key];
      if (value !== undefined && value !== null) {
        const stringValue = String(value);
        formValues[key] = value;
        const lowerKey = key.toLowerCase();
        if (lowerKey in localValues) {
          localValues[lowerKey] = stringValue;
        }
        compareForm.setFieldState(key, state => {
          state.value = value;
        });
      }
    });

    // Set values using setValuesIn for better reactivity
    compareForm.setValues(formValues);
    // Update local state for reactivity
    setModalFormValues(localValues);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setEditingPropertyId(null);
    compareForm.reset();
    // Reset local form values
    setModalFormValues({
      propertyname: "",
      persqftprice: "",
      bed: "",
      sqfootage: "",
      address: "",
      farfrom: "",
      notes: "",
      yearbuilt: "",
      yearsold: "",
      bath: "",
      salesprice: "",
    });
  };

  const getTableColumns = () => {
    if (!columnGridData?.grid) {
      console.log("No column grid data available");
      return [];
    }

    // System/metadata fields to exclude
    const excludedFields = [
      "id",
      "created_at",
      "updated_at",
      "created_on",
      "updated_on",
      "created_by",
      "updated_by",
      "is_active",
      "propertymaster_id", // Exclude the foreign key
    ];

    // Get columns from schema (not from data)
    const columns = columnGridData.grid
      .filter((field: any) => {
        const fieldName = field.field_name.toLowerCase();
        return !excludedFields.some(
          excluded => excluded.toLowerCase() === fieldName
        );
      })
      .map((field: any) => ({
        key: field.field_name.toLowerCase(),
        label:
          field.field_title ??
          field.field_name
            .replace(/([A-Z])/g, " $1") // Add space before capital letters
            .replace(/^./, (str: string) => str.toUpperCase()) // Capitalize first letter
            .trim(),
      }));

    console.log("Generated columns from schema:", columns);
    return columns;
  };

  // Calculate Price per Sq.Ft = (total sales price) / (total sq.footage) from comparison properties only
  const calculatePricePerSqFt = useCallback(() => {
    if (comparisonProperties.length === 0) {
      return null;
    }

    // Find field names (case-insensitive)
    const salespriceKey =
      comparisonProperties.length > 0
        ? Object.keys(comparisonProperties[0] ?? {}).find(
            key => key.toLowerCase() === "salesprice"
          )
        : null;

    const sqfootageKey =
      comparisonProperties.length > 0
        ? Object.keys(comparisonProperties[0] ?? {}).find(
            key => key.toLowerCase() === "sqfootage"
          )
        : null;

    if (!salespriceKey || !sqfootageKey) {
      return null;
    }

    // Sum all sales prices and sq.footage from comparison properties
    let totalSalesPrice = 0;
    let totalSqFootage = 0;
    let validCount = 0;

    comparisonProperties.forEach(property => {
      const salesPriceValue = property[salespriceKey];
      const sqFootageValue = property[sqfootageKey];

      const salesPriceNum =
        typeof salesPriceValue === "string"
          ? parseFloat(salesPriceValue)
          : salesPriceValue;
      const sqFootageNum =
        typeof sqFootageValue === "string"
          ? parseFloat(sqFootageValue)
          : sqFootageValue;

      if (
        typeof salesPriceNum === "number" &&
        !isNaN(salesPriceNum) &&
        typeof sqFootageNum === "number" &&
        !isNaN(sqFootageNum) &&
        sqFootageNum > 0
      ) {
        totalSalesPrice += salesPriceNum;
        totalSqFootage += sqFootageNum;
        validCount++;
      }
    });

    if (validCount === 0 || totalSqFootage === 0) {
      return null;
    }

    // Calculate Price per Sq.Ft = total sales price / total sq.footage
    const pricePerSqFt = totalSalesPrice / totalSqFootage;

    return pricePerSqFt;
  }, [comparisonProperties]);

  // Calculate average Per Sqft Price (persqftprice) across comparison properties only
  const calculateAvgPPSP = useCallback(() => {
    if (comparisonProperties.length === 0) {
      return null;
    }

    // Find the field name for persqftprice in comparison properties (case-insensitive)
    const persqftpriceKey =
      comparisonProperties.length > 0
        ? Object.keys(comparisonProperties[0] ?? {}).find(
            key => key.toLowerCase() === "persqftprice"
          )
        : null;

    if (!persqftpriceKey) {
      return null;
    }

    // Collect all valid prices: main property + comparison properties
    const validPrices: number[] = [];

    comparisonProperties.forEach(property => {
      const value = property[persqftpriceKey];
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      if (typeof numValue === "number" && !isNaN(numValue)) {
        validPrices.push(numValue);
      }
    });

    if (validPrices.length === 0) {
      return null;
    }

    // Calculate average
    const sum = validPrices.reduce((acc, price) => acc + price, 0);
    const average = sum / validPrices.length;

    return average;
  }, [comparisonProperties]); // Dependency: comparisonProperties

  // Calculate ARV = avgPPSP * sqfootage (from main property)
  const calculateARV = useCallback(() => {
    const avgPPSP = calculateAvgPPSP();
    if (avgPPSP === null || !recordInfo) {
      return null;
    }

    // Get main property's sqfootage (case-insensitive search)
    const sqfootageKey = Object.keys(recordInfo ?? {}).find(
      key => key.toLowerCase() === "sqfootage"
    );

    if (!sqfootageKey) {
      return null;
    }

    const sqfootageValue = recordInfo[sqfootageKey];
    const sqfootageNum =
      typeof sqfootageValue === "string"
        ? parseFloat(sqfootageValue)
        : sqfootageValue;

    if (typeof sqfootageNum !== "number" || isNaN(sqfootageNum)) {
      return null;
    }

    // Calculate ARV = average PPSP * sqfootage
    const arv = avgPPSP * sqfootageNum;

    return arv;
  }, [calculateAvgPPSP, recordInfo]); // Dependencies: calculateAvgPPSP, recordInfo

  // State to track calculated ARV for display
  const [calculatedARVValue, setCalculatedARVValue] = useState<number | null>(
    null
  );

  // Disabled auto-save ARV to prevent 400 errors
  // ARV will be calculated and displayed, but saved only when user manually saves the form

  // Update finalarv field in form when ARV is calculated (but don't auto-save to avoid errors)
  useEffect(() => {
    if (!recordInfo) {
      setCalculatedARVValue(null);
      return;
    }
    const calculatedARV = calculateARV();
    const arvValue =
      typeof calculatedARV === "number" && !Number.isNaN(calculatedARV)
        ? calculatedARV
        : null;

    // Update state for display
    setCalculatedARVValue(arvValue);

    // Update form field state
    form.setFieldState("finalarv", state => {
      state.value = arvValue ?? null;
    });

    // Also update form values directly to ensure it's reflected
    if (arvValue !== null) {
      form.setValuesIn("finalarv", arvValue);
    }

    // Disabled auto-save to prevent 400 errors
    // ARV will be saved when user manually saves the form
    // if (arvValue !== lastSavedArvRef.current) {
    //   void saveARVToPropertyMaster(arvValue);
    // }
  }, [recordInfo, form, calculateARV, comparisonProperties]); // Added comparisonProperties to dependencies

  // Helper function to get field value from form (handles nested structures and case-insensitive matching)
  const getComparisonFieldValue = (formValues: any, fieldName: string): any => {
    // Helper to find field by case-insensitive name
    const findFieldValue = (obj: any, searchName: string): any => {
      if (!obj || typeof obj !== "object") {
        return undefined;
      }

      // First try exact match
      if (obj[searchName] !== undefined) {
        return obj[searchName];
      }

      // Then try case-insensitive match
      const lowerSearchName = searchName.toLowerCase();
      for (const key in obj) {
        if (key.toLowerCase() === lowerSearchName) {
          return obj[key];
        }
      }

      return undefined;
    };

    // Check top level first
    const topLevelValue = findFieldValue(formValues, fieldName);
    if (topLevelValue !== undefined) {
      return topLevelValue;
    }

    // Check nested structures
    const nestedPaths = [
      "basicInfo",
      "propertyDetails",
      "financialDetails",
      "basicGrid",
      "propertyGrid",
      "financialGrid",
      "layoutContainer",
    ];
    for (const path of nestedPaths) {
      if (formValues[path]) {
        const nestedValue = findFieldValue(formValues[path], fieldName);
        if (nestedValue !== undefined) {
          return nestedValue;
        }
      }
    }

    // Also check all keys recursively (in case field is deeply nested)
    const checkRecursively = (obj: any, searchName: string, depth = 0): any => {
      if (depth > 3 || !obj || typeof obj !== "object" || Array.isArray(obj)) {
        return undefined;
      }

      const value = findFieldValue(obj, searchName);
      if (value !== undefined) {
        return value;
      }

      // Recursively check nested objects
      for (const key in obj) {
        if (typeof obj[key] === "object" && obj[key] !== null) {
          const found = checkRecursively(obj[key], searchName, depth + 1);
          if (found !== undefined) {
            return found;
          }
        }
      }

      return undefined;
    };

    return checkRecursively(formValues, fieldName);
  };

  const handleComparePropertySave = async () => {
    try {
      // Validate form
      await compareForm.validate();

      // Additional check: ensure required fields have values
      const formValues = compareForm.values;
      const missingFields: string[] = [];

      // Debug: Log form values to see what we're working with
      console.log(
        "[Comparison Property] Form values:",
        JSON.stringify(formValues, null, 2)
      );

      requiredComparisonFields.forEach(fieldName => {
        const value = getComparisonFieldValue(formValues, fieldName);

        // Debug: Log each field check
        console.log(
          `[Comparison Property] Checking field "${fieldName}":`,
          value,
          "Type:",
          typeof value
        );

        // Check if field is empty
        // Note: 0 is a valid value for numeric fields, so we should allow it
        const isEmpty =
          value === undefined ||
          value === null ||
          value === "" ||
          (typeof value === "string" && value.trim() === "") ||
          (typeof value === "number" && isNaN(value));

        if (isEmpty) {
          missingFields.push(getComparisonFieldLabel(fieldName));
          console.log(
            `[Comparison Property] Field "${fieldName}" is missing or empty`
          );
        }
      });

      if (missingFields.length > 0) {
        // Log all form values to help debug
        console.log("[Comparison Property] Missing fields:", missingFields);
        console.log("[Comparison Property] All form values:", formValues);
        toast.error(
          `Please fill in all required fields: ${missingFields.join(", ")}`
        );
        return;
      }

      // Get the form values
      const savedData = removeMetaFields(compareForm.values);
      if (!id) {
        notify.error("Property ID missing. Please reopen the page.");
        return;
      }

      if (editingPropertyId) {
        // Update existing record
        await DynamicModuleService.updateItem(
          comparePropertySchemaName,
          editingPropertyId,
          {
            ...savedData,
            propertymaster_id: id,
          }
        );
        toast.success("Comparison property updated successfully!");
      } else {
        // Create new record
        await DynamicModuleService.createItem(comparePropertySchemaName, {
          ...savedData,
          propertymaster_id: id,
        });
        toast.success("Comparison property added successfully!");
      }

      setIsModalOpen(false);
      setEditingPropertyId(null);
      compareForm.reset();

      // Invalidate and refetch comparison properties to update the table
      queryClient.invalidateQueries({
        queryKey: ["comparison-properties", comparePropertySchemaName],
      });
      await refetchComparisonProperties();
    } catch (error: any) {
      // Extract error message
      let errorMessage =
        "Failed to save comparison property. Please check the form.";

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data) {
        const data = error.response.data;
        errorMessage =
          data?.error?.message ??
          data?.message ??
          data?.error ??
          data?.non_field_errors?.[0] ??
          errorMessage;
      }

      toast.error(errorMessage);
      console.error("Save error:", error);
    }
  };

  const handleDeleteComparisonProperty = async (property: any) => {
    // Try to find the ID field (case-insensitive)
    const propertyId =
      property.id ??
      property.Id ??
      property.ID ??
      property._id ??
      property._Id ??
      property._ID ??
      (() => {
        // Try to find any field that looks like an ID
        const idKey = Object.keys(property ?? {}).find(
          key => key.toLowerCase() === "id"
        );
        return idKey ? property[idKey] : null;
      })();

    if (!propertyId) {
      console.error("Property object:", property);
      console.error("Available keys:", Object.keys(property ?? {}));
      notify.error("Cannot delete: Property ID not found");
      return;
    }

    try {
      await DynamicModuleService.deleteItem(
        comparePropertySchemaName,
        String(propertyId)
      );
      toast.success("Comparison property removed");

      // Invalidate and refetch comparison properties to update the table
      queryClient.invalidateQueries({
        queryKey: ["comparison-properties", comparePropertySchemaName],
      });

      // Refetch with error handling
      try {
        await refetchComparisonProperties();
      } catch (refetchError) {
        console.error("Error refetching comparison properties:", refetchError);
        // Don't show error to user as the delete was successful
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Failed to delete comparison property";
      notify.error(errorMessage);
    }
  };

  // Get form values for display - prioritize recordInfo if form values are not set yet
  const getFormValue = (fieldName: string): string => {
    // For finalarv, use calculated ARV value if available (prioritize calculated value)
    if (fieldName.toLowerCase() === "finalarv") {
      if (calculatedARVValue !== null && !Number.isNaN(calculatedARVValue)) {
        return String(calculatedARVValue);
      }
      // Fallback to form value or recordInfo
      let value = form.values[fieldName];
      if (
        (value === undefined || value === null || value === "") &&
        recordInfo
      ) {
        const recordKey = Object.keys(recordInfo ?? {}).find(
          key => key.toLowerCase() === "finalarv"
        );
        if (recordKey) {
          value = recordInfo[recordKey];
        }
      }
      if (value === undefined || value === null || value === "") {
        return "";
      }
      return String(value);
    }

    // First try form values
    let value = form.values[fieldName];

    // If form value is empty and recordInfo is available, use recordInfo
    if ((value === undefined || value === null || value === "") && recordInfo) {
      // Try case-insensitive lookup in recordInfo
      const recordKey = Object.keys(recordInfo ?? {}).find(
        key => key.toLowerCase() === fieldName.toLowerCase()
      );
      if (recordKey) {
        value = recordInfo[recordKey];
      }
    }

    if (value === undefined || value === null || value === "") {
      return "";
    }
    return String(value);
  };

  // Helper function to get field error message from Formily form
  const getFieldError = (fieldName: string): string | undefined => {
    const fieldState = compareForm.getFieldState(fieldName);
    if (fieldState?.errors && fieldState.errors.length > 0) {
      const error = fieldState.errors[0];
      if (typeof error === "string") {
        return error;
      }
      // Handle IFormFeedback type
      if (error && typeof error === "object") {
        return error.messages?.[0] ?? String(error);
      }
      return String(error);
    }
    return undefined;
  };

  return (
    <Layout>
      <div className="max-w-5xl space-y-4 lg:space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Property Information
          </h1>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Hidden SchemaFormRender to maintain form state */}
          <div style={{ display: "none" }}>
            <SchemaFormRender form={form} schemaJson={restrictedSchema} />
          </div>

          {/* Property Details Card */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="propertyname">Property name:</Label>
                  <Input
                    id="propertyname"
                    value={getFormValue("propertyname")}
                    disabled
                    readOnly
                    placeholder="Property name"
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="persqftprice">Per Sqft Price:</Label>
                  <Input
                    id="persqftprice"
                    type="number"
                    value={getFormValue("persqftprice")}
                    disabled
                    readOnly
                    placeholder="e.g., 150"
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="yearbuilt">Year built:</Label>
                  <Input
                    id="yearbuilt"
                    type="number"
                    value={getFormValue("yearbuilt")}
                    disabled
                    readOnly
                    placeholder="e.g., 2005"
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bed">Bed:</Label>
                  <Input
                    id="bed"
                    type="number"
                    value={getFormValue("bed")}
                    disabled
                    readOnly
                    placeholder="e.g., 3"
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bath">Bath:</Label>
                  <Input
                    id="bath"
                    type="number"
                    value={getFormValue("bath")}
                    disabled
                    readOnly
                    placeholder="e.g., 2"
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sqfootage">Sq. Footage:</Label>
                  <Input
                    id="sqfootage"
                    type="number"
                    value={getFormValue("sqfootage")}
                    disabled
                    readOnly
                    placeholder="e.g., 1500"
                    className="bg-muted"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Details Card */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Financial Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="rougharv">Rough ARV:</Label>
                    <Input
                      id="rougharv"
                      type="number"
                      value={getFormValue("rougharv")}
                      onChange={e => {
                        form.setFieldState("rougharv", state => {
                          state.value = e.target.value;
                        });
                      }}
                      placeholder="Rough ARV"
                      disabled
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="finalarv">Final ARV:</Label>
                    <Input
                      id="finalarv"
                      type="number"
                      value={getFormValue("finalarv")}
                      disabled
                      placeholder="Calculated automatically"
                      className="bg-muted"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes:</Label>
                  <Textarea
                    id="notes"
                    value={getFormValue("notes")}
                    onChange={e => {
                      form.setFieldState("notes", state => {
                        state.value = e.target.value;
                      });
                    }}
                    placeholder="Additional notes or comments..."
                    rows={4}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Values */}
          {(() => {
            const pricePerSqFt = calculatePricePerSqFt();
            const avgPPSP = calculateAvgPPSP();
            const arv = calculateARV();

            if (pricePerSqFt !== null || avgPPSP !== null || arv !== null) {
              return (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {pricePerSqFt !== null && (
                    <div className="rounded-lg border bg-muted/50 p-4">
                      <div className="text-sm font-semibold text-muted-foreground">
                        Price per Sq ft:
                      </div>
                      <div className="mt-1 text-xl font-bold text-foreground lg:text-2xl">
                        {pricePerSqFt.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {avgPPSP !== null && (
                    <div className="rounded-lg border bg-muted/50 p-4">
                      <div className="text-sm font-semibold text-muted-foreground">
                        AVG PPSF:
                      </div>
                      <div className="mt-1 text-xl font-bold text-foreground lg:text-2xl">
                        {avgPPSP.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {arv !== null && (
                    <div className="rounded-lg border bg-muted/50 p-4">
                      <div className="text-sm font-semibold text-muted-foreground">
                        ARV:
                      </div>
                      <div className="mt-1 text-xl font-bold text-foreground lg:text-2xl">
                        {arv.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </form>
        {/* Comparison Properties Table */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>
              Comparison Properties ({comparisonProperties.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {comparisonProperties.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No comparison properties added yet. Click &quot;+ Compare
                Property +&quot; to add one.
              </div>
            ) : (
              (() => {
                const columns = getTableColumns();
                return columns.length === 0 ? (
                  <div className="py-4 text-center text-muted-foreground">
                    No data fields found. Check console for details.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          {columns.map(
                            (col: { key: string; label: string }) => (
                              <TableHead
                                key={col.key}
                                className="font-semibold"
                              >
                                {col.label}
                              </TableHead>
                            )
                          )}
                          <TableHead className="text-right font-semibold">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {comparisonProperties.map((property, index) => (
                          <TableRow key={property.id ?? index}>
                            {columns.map(
                              (col: { key: string; label: string }) => (
                                <TableCell key={col.key}>
                                  {property[col.key] !== null &&
                                  property[col.key] !== undefined
                                    ? String(property[col.key])
                                    : "-"}
                                </TableCell>
                              )
                            )}
                            <TableCell>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleEditComparisonProperty(property)
                                  }
                                  title="Edit"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleDeleteComparisonProperty(property)
                                  }
                                  title="Delete"
                                  className="hover:bg-red-500 hover:text-red-100 dark:hover:bg-red-950"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>
        <div className="flex gap-4">
          <Button
            type="button"
            onClick={handleCompareProperty}
            className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
          >
            + Compare Property +
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(navigationRoutes.COMPARISON_PROPERTY_MASTER)
            }
          >
            Cancel
          </Button>
        </div>
        {/* Compare Property Modal */}
        <CommonModal
          open={isModalOpen}
          onCancel={handleModalCancel}
          title={
            editingPropertyId
              ? "Edit Comparison Property"
              : "Add Comparison Property"
          }
          width={800}
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleComparePropertySave}
                className="bg-[#4a5273] text-white hover:bg-[#4a5273]/90"
              >
                {editingPropertyId ? "Update" : "Save"}
              </Button>
            </div>
          }
          destroyOnClose
        >
          <div className="mt-4">
            {/* Hidden SchemaFormAutoSubmit for form submission */}
            <div style={{ display: "none" }}>
              <SchemaFormAutoSubmit
                form={compareForm}
                schemaName={comparePropertySchemaName}
                ref={compareFormRef}
              />
            </div>

            {/* Manual Form Layout - Two Columns */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-propertyname">Property name:</Label>
                  <Input
                    id="modal-propertyname"
                    value={modalFormValues.propertyname}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({
                        ...prev,
                        propertyname: value,
                      }));
                      compareForm.setValuesIn("propertyname", value);
                      compareForm.setFieldState("propertyname", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("propertyname");
                    }}
                    placeholder="Property name"
                    className={
                      getFieldError("propertyname") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("propertyname") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("propertyname")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-persqftprice">Per Sqft Price:</Label>
                  <Input
                    id="modal-persqftprice"
                    type="number"
                    value={modalFormValues.persqftprice}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({
                        ...prev,
                        persqftprice: value,
                      }));
                      compareForm.setValuesIn("persqftprice", value);
                      compareForm.setFieldState("persqftprice", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("persqftprice");
                    }}
                    placeholder="Per Sqft Price"
                    className={
                      getFieldError("persqftprice") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("persqftprice") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("persqftprice")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-bed">Bed:</Label>
                  <Input
                    id="modal-bed"
                    type="number"
                    value={modalFormValues.bed}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({ ...prev, bed: value }));
                      compareForm.setValuesIn("bed", value);
                      compareForm.setFieldState("bed", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("bed");
                    }}
                    placeholder="Bed"
                    className={getFieldError("bed") ? "border-destructive" : ""}
                  />
                  {getFieldError("bed") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("bed")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-sqfootage">Sq. Footage:</Label>
                  <Input
                    id="modal-sqfootage"
                    type="number"
                    value={modalFormValues.sqfootage}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({
                        ...prev,
                        sqfootage: value,
                      }));
                      compareForm.setValuesIn("sqfootage", value);
                      compareForm.setFieldState("sqfootage", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("sqfootage");
                    }}
                    placeholder="Sq. Footage"
                    className={
                      getFieldError("sqfootage") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("sqfootage") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("sqfootage")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-address">Address:</Label>
                  <GoogleAddressInput
                    value={modalFormValues.address}
                    onChange={(details?: any) => {
                      const formattedAddress =
                        typeof details === "string"
                          ? details
                          : (details?.formatted_address ??
                            details?.display_name ??
                            details?.name ??
                            "");
                      setModalFormValues(prev => ({
                        ...prev,
                        address: formattedAddress,
                      }));
                      handleCompareAddressChange(details);
                    }}
                    placeholder="Enter property address"
                    types={["establishment", "geocode"]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-farfrom">Far from main property:</Label>
                  <Input
                    id="modal-farfrom"
                    type="number"
                    value={modalFormValues.farfrom}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({ ...prev, farfrom: value }));
                      compareForm.setValuesIn("farfrom", value);
                      compareForm.setFieldState("farfrom", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("farfrom");
                    }}
                    placeholder="Far from main property"
                    className={
                      getFieldError("farfrom") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("farfrom") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("farfrom")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-notes">Notes:</Label>
                  <Textarea
                    id="modal-notes"
                    value={modalFormValues.notes}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({ ...prev, notes: value }));
                      compareForm.setValuesIn("notes", value);
                      compareForm.setFieldState("notes", state => {
                        state.value = value;
                      });
                    }}
                    placeholder="Notes"
                    rows={4}
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="modal-yearbuilt">Year built:</Label>
                  <Input
                    id="modal-yearbuilt"
                    type="number"
                    value={modalFormValues.yearbuilt}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({
                        ...prev,
                        yearbuilt: value,
                      }));
                      compareForm.setValuesIn("yearbuilt", value);
                      compareForm.setFieldState("yearbuilt", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("yearbuilt");
                    }}
                    placeholder="Year built"
                    className={
                      getFieldError("yearbuilt") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("yearbuilt") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("yearbuilt")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-yearsold">Year sold:</Label>
                  <Input
                    id="modal-yearsold"
                    type="number"
                    value={modalFormValues.yearsold}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({
                        ...prev,
                        yearsold: value,
                      }));
                      compareForm.setValuesIn("yearsold", value);
                      compareForm.setFieldState("yearsold", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("yearsold");
                    }}
                    placeholder="Year sold"
                    className={
                      getFieldError("yearsold") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("yearsold") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("yearsold")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-bath">Bath:</Label>
                  <Input
                    id="modal-bath"
                    type="number"
                    value={modalFormValues.bath}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({ ...prev, bath: value }));
                      compareForm.setValuesIn("bath", value);
                      compareForm.setFieldState("bath", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("bath");
                    }}
                    placeholder="Bath"
                    className={
                      getFieldError("bath") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("bath") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("bath")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modal-salesprice">Sales price:</Label>
                  <Input
                    id="modal-salesprice"
                    type="number"
                    value={modalFormValues.salesprice}
                    onChange={e => {
                      const value = e.target.value;
                      setModalFormValues(prev => ({
                        ...prev,
                        salesprice: value,
                      }));
                      compareForm.setValuesIn("salesprice", value);
                      compareForm.setFieldState("salesprice", state => {
                        state.value = value;
                      });
                    }}
                    onBlur={() => {
                      compareForm.validate("salesprice");
                    }}
                    placeholder="Sales price"
                    className={
                      getFieldError("salesprice") ? "border-destructive" : ""
                    }
                  />
                  {getFieldError("salesprice") && (
                    <p className="text-sm text-destructive">
                      {getFieldError("salesprice")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CommonModal>
      </div>
    </Layout>
  );
};

export default PP_EditComparisonProperty;
